// ============================================================================
// marketOperator.ts — ДАННЫЕ операторского экрана маркетплейса.
//
// Только существующие серверные механизмы (без новых RPC/полей/миграций):
//   • мастера на проверке — справочник mkt_masters через ListTableData;
//     для оператора сервер добавляет verification_state/reason/comment,
//     verified_by_name/verified_at (internal/markettake/verify.go);
//   • решение по мастеру — UpdateTableRow с колонками-командами
//     __action=verify_master|reject_master (+ verify_reason/verify_comment);
//   • возврат по заявке — UpdateTableRow документа mkt_requests с
//     __action=refund (+ refund_reason/refund_comment);
//   • документы мастера — штатные «Файлы» (files.listFiles, ownerId=мастер);
//   • архив заявок — статусы, отличные от активных (new/taken/in_progress),
//     включая архивный код базы (по умолчанию expired).
//
// Финансовые/проверочные операции НЕ уходят в офлайн-очередь: без связи
// возвращается явная ошибка, а не «успех», которого не было на сервере.
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { bytesToBlob } from '@/lib/fileBytes';
import {
  errorCode,
  friendlyError,
  isNotFoundError,
  isPermissionDeniedError,
  isTransportError,
  logSwallowed,
} from '@/lib/errors';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { marketGeoFromCells } from '@/lib/marketGeo';
import {
  cellsOf,
  listTableRows,
  loadMarketCategories,
  loadMarketStatusLabels,
  type MarketRequest,
} from '@/lib/market';
import { MARKET_MASTER_TABLE, MARKET_REQUEST_TABLE } from '@/lib/marketConstants';

/** Статусы, в которых заявка ещё активна (всё остальное — архив). */
const ACTIVE_REQUEST_STATUSES = new Set(['new', 'taken', 'in_progress']);

/** Подписи статусов, которых может не быть в перечислении базы. */
const STATUS_FALLBACK_LABELS: Record<string, string> = {
  expired: 'В архиве',
  done: 'Выполнена',
  canceled: 'Отменена',
};

export type MasterReviewDecision = 'approve' | 'reject';

export type MasterReviewState = 'pending' | 'rejected';

export interface MarketMasterReview {
  id: string;
  name: string;
  phone: string;
  email: string;
  about: string;
  userId: string;
  state: MasterReviewState;
  /** Последняя причина отказа (из журнала решений сервера). */
  reason: string;
  comment: string;
  decidedByName: string;
  decidedAt: string;
  createdAt: string;
  /** Средняя оценка мастера (только опубликованные отзывы). */
  rating: number | null;
  /** Количество опубликованных отзывов. */
  reviewsCount: number | null;
}

export interface MarketAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface MarketOperatorRequest extends MarketRequest {
  updatedAt: string;
  takePriceValue: number | null;
}

export type MarketOperatorFailureKind = 'permission' | 'offline' | 'invalid' | 'not-found' | 'error';

export interface MarketOperatorFailure {
  kind: MarketOperatorFailureKind;
  message: string;
}

export type MarketOperatorActionResult =
  | { ok: true; already: boolean }
  | { ok: false; kind: MarketOperatorFailureKind; message: string };

function isTrue(value: string | undefined): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

function numberOf(value: string | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function masterFromRow(row: TableDataRow): MarketMasterReview {
  const cells = cellsOf(row);
  const state = (cells.get('verification_state') || '').toLowerCase();
  return {
    id: row.id,
    name: cells.get('name') || 'Мастер без имени',
    phone: cells.get('phone') || '',
    email: cells.get('email') || '',
    about: cells.get('about') || '',
    userId: cells.get('entity_id') || '',
    state: state === 'rejected' ? 'rejected' : 'pending',
    reason: cells.get('verification_reason') || '',
    comment: cells.get('verification_comment') || '',
    decidedByName: cells.get('verified_by_name') || '',
    decidedAt: cells.get('verified_at') || '',
    createdAt: cells.get('created_at') || '',
    rating: numberOf(cells.get('rating_avg')),
    reviewsCount: numberOf(cells.get('reviews_count')),
  };
}

/**
 * Мастера, ожидающие решения (не подтверждённые и не помеченные на удаление).
 * Строки уже содержат последнее решение, причина отказа видна оператору.
 */
export async function loadMarketMastersForReview(): Promise<MarketMasterReview[]> {
  const rows = await listTableRows(MARKET_MASTER_TABLE, {
    pageSize: 200,
    orderBy: 'created_at',
    orderDesc: true,
  });
  return rows
    .filter((row) => {
      const cells = cellsOf(row);
      return !isTrue(cells.get('verified')) && !isTrue(cells.get('is_marked'));
    })
    .map(masterFromRow)
    .sort((a, b) => {
      if (a.state !== b.state) return a.state === 'pending' ? -1 : 1;
      return b.createdAt.localeCompare(a.createdAt);
    });
}

interface FileListResponse {
  items?: Array<Record<string, unknown>>;
}

/** Документы мастера — штатные «Файлы», привязанные к записи справочника. */
export async function loadMarketMasterDocuments(masterId: string): Promise<MarketAttachment[]> {
  if (!masterId) return [];
  const res = (await getSDK().files.listFiles({
    ownerId: masterId,
    includeLinked: false,
  })) as FileListResponse;
  return (res.items || [])
    .map((f) => ({
      id: String(f.id || ''),
      fileName: String(f.fileName || 'Документ'),
      fileType: String(f.fileType || ''),
      fileSize: Number(f.fileSize || 0),
    }))
    .filter((f) => f.id);
}

function requestFromRow(
  row: TableDataRow,
  categories: Map<string, { name: string }>,
  statusLabels: Record<string, string>,
): MarketOperatorRequest {
  const cells = cellsOf(row);
  const categoryId = cells.get('category_id') || '';
  const status = cells.get('status') || '';
  const takePrice = cells.get('take_price') || '';
  return {
    id: row.id,
    number: cells.get('number') || '',
    categoryId,
    categoryName: cells.get('category_id_presentation') || categories.get(categoryId)?.name || '',
    masterId: cells.get('master_id') || '',
    masterName: cells.get('master_id_presentation') || cells.get('master_name') || '',
    status,
    statusLabel: statusLabels[status] || STATUS_FALLBACK_LABELS[status] || status,
    descr: cells.get('descr') || '',
    address: cells.get('address') || '',
    phone: cells.get('phone') || '',
    createdAt: cells.get('created_at') || '',
    createdBy: cells.get('created_by') || '',
    takePrice,
    takePriceValue: numberOf(takePrice),
    contactRevealed: isTrue(cells.get('contact_revealed')),
    isMarked: isTrue(cells.get('is_marked')),
    phoneVerified: isTrue(cells.get('phone_verified')),
    phoneUnverified: isTrue(cells.get('phone_unverified')),
    phoneVerificationHint: cells.get('phone_verification_hint') || '',
    geo: marketGeoFromCells(cells),
    masterCatalogId: cells.get('master_catalog_id') || '',
    masterRating: numberOf(cells.get('master_rating')),
    masterReviewsCount: numberOf(cells.get('master_reviews_count')),
    updatedAt: cells.get('updated_at') || '',
  };
}

async function operatorAuxiliary(): Promise<{
  categories: Map<string, { name: string }>;
  statusLabels: Record<string, string>;
}> {
  let categories = new Map<string, { name: string }>();
  let statusLabels: Record<string, string> = {};
  try {
    const list = await loadMarketCategories();
    categories = new Map(list.map((c) => [c.id, { name: c.name }]));
  } catch (e) {
    logSwallowed(e, 'Оператор маркетплейса: категории заявок');
  }
  try {
    statusLabels = await loadMarketStatusLabels();
  } catch (e) {
    logSwallowed(e, 'Оператор маркетплейса: подписи статусов заявок');
  }
  return { categories, statusLabels };
}

/** Все заявки для оператора: возвраты (оплаченные) и архив. */
export async function loadMarketOperatorRequests(): Promise<MarketOperatorRequest[]> {
  const [rows, auxiliary] = await Promise.all([
    listTableRows(MARKET_REQUEST_TABLE, {
      pageSize: 200,
      orderBy: 'created_at',
      orderDesc: true,
    }),
    operatorAuxiliary(),
  ]);
  return rows.map((row) => requestFromRow(row, auxiliary.categories, auxiliary.statusLabels));
}

/**
 * Заявки ПО ИДЕНТИФИКАТОРАМ (очередь споров/адресные карточки): читаем точечно,
 * а не «первые 200» — спор может быть открыт по старой заявке.
 */
export async function loadMarketOperatorRequestsByIds(ids: string[]): Promise<MarketOperatorRequest[]> {
  const unique = Array.from(new Set(ids.filter(Boolean))).slice(0, 100);
  if (unique.length === 0) return [];
  const auxiliary = await operatorAuxiliary();
  const rows: Array<TableDataRow | undefined> = [];
  // Пачками по 8: очередь споров не должна открывать 100 запросов разом.
  for (let i = 0; i < unique.length; i += 8) {
    const batch = await Promise.all(unique.slice(i, i + 8).map(async (id) => {
      const found = await listTableRows(MARKET_REQUEST_TABLE, {
        pageSize: 1,
        filters: [{ column: 'id', value: id }],
      });
      return found[0];
    }));
    rows.push(...batch);
  }
  return rows
    .filter((row): row is TableDataRow => !!row)
    .map((row) => requestFromRow(row, auxiliary.categories, auxiliary.statusLabels));
}

/** Заявка оплачена (было взятие) — по ней возможен возврат. */
export function isMarketRefundCandidate(request: MarketOperatorRequest): boolean {
  return request.contactRevealed && !!request.masterId;
}

/** Архив: заявка не в активном статусе (done/canceled/expired и коды базы). */
export function isMarketArchivedRequest(request: MarketOperatorRequest): boolean {
  const status = String(request.status || '').trim().toLowerCase();
  return status !== '' && !ACTIVE_REQUEST_STATUSES.has(status);
}

function toFailure(e: unknown, fallback: string): MarketOperatorFailure {
  const message = friendlyError(e).trim() || fallback;
  if (isTransportError(e)) return { kind: 'offline', message };
  if (isPermissionDeniedError(e)) return { kind: 'permission', message };
  if (isNotFoundError(e)) return { kind: 'not-found', message };
  const code = errorCode(e);
  if (code === 3 || code === 'invalid_argument' || code === 'InvalidArgument') {
    return { kind: 'invalid', message };
  }
  return { kind: 'error', message };
}

function offlineFailure(message: string): MarketOperatorActionResult {
  return { ok: false, kind: 'offline', message };
}

/**
 * Решение по мастеру через существующий UpdateTableRow.
 * Причина обязательна при отказе; подтверждение допускает комментарий.
 */
export async function reviewMarketMaster(
  masterId: string,
  decision: MasterReviewDecision,
  reason: string,
  comment: string,
): Promise<MarketOperatorActionResult> {
  const cleanReason = reason.trim();
  const cleanComment = comment.trim();
  if (!masterId) return { ok: false, kind: 'invalid', message: 'Не указан мастер' };
  if (decision === 'reject' && !cleanReason) {
    return { ok: false, kind: 'invalid', message: 'Для отказа укажите причину' };
  }
  if (isEffectivelyOffline()) {
    return offlineFailure('Проверка мастера требует связи с сервером');
  }
  const values: Array<{ column: string; value: string }> = [
    { column: '__action', value: decision === 'approve' ? 'verify_master' : 'reject_master' },
  ];
  if (cleanReason) values.push({ column: 'verify_reason', value: cleanReason });
  if (cleanComment) values.push({ column: 'verify_comment', value: cleanComment });
  try {
    const res = (await getSDK().metadata.updateTableRow({
      schema: 'public',
      table: MARKET_MASTER_TABLE,
      rowId: masterId,
      values,
    })) as { row?: TableDataRow };
    const row = res?.row;
    const already = row ? isTrue(cellsOf(row).get('verification_already')) : false;
    return { ok: true, already };
  } catch (e) {
    const failure = toFailure(e, 'Не удалось сохранить решение по мастеру');
    return { ok: false, ...failure };
  }
}

/**
 * Возврат стоимости взятия заявки мастеру. Сумма и счёт определяются
 * сервером по фактическому списанию — клиент их не передаёт.
 */
export async function refundMarketRequest(
  requestId: string,
  reason: string,
  comment: string,
): Promise<MarketOperatorActionResult> {
  const cleanReason = reason.trim();
  const cleanComment = comment.trim();
  if (!requestId) return { ok: false, kind: 'invalid', message: 'Не указана заявка' };
  if (!cleanReason) return { ok: false, kind: 'invalid', message: 'Укажите причину возврата' };
  if (!cleanComment) return { ok: false, kind: 'invalid', message: 'Укажите комментарий к возврату' };
  if (isEffectivelyOffline()) {
    return offlineFailure('Возврат денег требует связи с сервером');
  }
  try {
    await getSDK().metadata.updateTableRow({
      schema: 'public',
      table: MARKET_REQUEST_TABLE,
      rowId: requestId,
      values: [
        { column: '__action', value: 'refund' },
        { column: 'refund_reason', value: cleanReason },
        { column: 'refund_comment', value: cleanComment },
      ],
    });
    return { ok: true, already: false };
  } catch (e) {
    const failure = toFailure(e, 'Не удалось выполнить возврат по заявке');
    return { ok: false, ...failure };
  }
}

/** Скачивание документа мастера (файл из штатного справочника «Файлы»). */
export async function downloadMarketAttachment(file: MarketAttachment): Promise<void> {
  if (!file.id) throw new Error('Не указан файл');
  const res = (await getSDK().files.downloadFile({ id: file.id })) as { data?: unknown };
  const blob = bytesToBlob(res?.data, file.fileType || 'application/octet-stream');
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName || 'file';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Размер файла для компактной строки документа. */
export function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
